"use client";

import { useEffect, useRef, useState, useSyncExternalStore } from "react";

interface VoiceQueryInputProps {
  onTranscript: (text: string) => void;
  language?: "hi" | "en";
  className?: string;
}

// Window speech recognition typings
interface SpeechRecognitionInstance {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  onresult: (event: {
    results: { [key: number]: { [key: number]: { transcript: string } } };
  }) => void;
  onerror: () => void;
  onend: () => void;
}

function checkSpeechSupport(): boolean {
  if (typeof window === "undefined") return false;
  return Boolean(
    (window as unknown as { SpeechRecognition?: unknown }).SpeechRecognition ||
    (window as unknown as { webkitSpeechRecognition?: unknown }).webkitSpeechRecognition
  );
}

// A browser capability is NOT knowable during the hydration render. Reading it
// there returns false on the server and true in the browser for the SAME render
// pass, so a component that gates `return null` on it emits an element the
// server never sent -- a structural mismatch (React #418). App Router hydrates
// the whole document, so ONE such mismatch regenerates the entire root, which
// is what produced the trashed head and made #1791/#1785 look like head
// injection for weeks.
//
// useSyncExternalStore is React's answer: it takes an explicit SERVER snapshot,
// so the first client render provably matches the server, and the real value
// arrives after mount. A capability never changes within a session, so the
// subscription is a no-op -- but it must be module-level and stable, or React
// resubscribes every render.
const NO_CHANGE = () => () => {};
const SERVER_SNAPSHOT = () => false;

export default function VoiceQueryInput({
  onTranscript,
  language = "hi",
  className = "",
}: VoiceQueryInputProps) {
  const [isListening, setIsListening] = useState(false);
  const isSupported = useSyncExternalStore(NO_CHANGE, checkSpeechSupport, SERVER_SNAPSHOT);
  const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);

  useEffect(() => {
    const SpeechRecognition =
      (window as unknown as { SpeechRecognition?: new () => SpeechRecognitionInstance })
        .SpeechRecognition ||
      (window as unknown as { webkitSpeechRecognition?: new () => SpeechRecognitionInstance })
        .webkitSpeechRecognition;

    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = language === "hi" ? "hi-IN" : "en-IN";

      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        if (transcript) {
          onTranscript(transcript);
        }
      };

      recognition.onerror = () => setIsListening(false);
      recognition.onend = () => setIsListening(false);
      recognitionRef.current = recognition;
    }
  }, [language, onTranscript]);

  // The control renders on the server and at hydration as well, disabled
  // until the capability snapshot arrives: gating `return null` on support
  // added a button (and its words) after hydration in every browser that has
  // speech recognition, so the hydrated page read differently from the server
  // HTML and the certification's word projection rejected the money pages.
  // Only the disabled state changes after mount, never the text.
  const toggleListening = () => {
    if (!recognitionRef.current) return;
    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch {
        setIsListening(false);
      }
    }
  };

  return (
    <button
      type="button"
      onClick={toggleListening}
      disabled={!isSupported}
      title={isSupported ? undefined : "Voice input is not available in this browser"}
      className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-caption font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
        isListening
          ? "border-intent-brand bg-intent-brand/10 text-intent-brand animate-pulse ring-2 ring-intent-brand/30"
          : "border-border-default bg-surface-raised text-text-secondary hover:border-border-interactive hover:text-text-primary"
      } ${className}`}
      aria-label={isListening ? "Listening to your voice..." : "Tap to speak your question"}
    >
      <span className="text-base">{isListening ? "🔴" : "🎙️"}</span>
      {/* The Hindi label is marked lang="hi" so an English page's screen
          reader voices it in Hindi (c-a11y-11). */}
      <span lang={language === "hi" ? "hi" : undefined}>
        {isListening
          ? language === "hi"
            ? "सुन रहे हैं... बोलिए"
            : "Listening..."
          : language === "hi"
            ? "बोलकर पूछें"
            : "Tap to Speak"}
      </span>
    </button>
  );
}
